import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useProducts } from "@/hooks/useProducts";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2 } from "lucide-react";
import { ProductForm } from "@/components/products/ProductForm";
import { ProductFormData } from "@/types/product";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useQuery } from "@tanstack/react-query";

export default function AdminNewProduct() {
  const navigate = useNavigate();
  const { storeId } = useParams<{ storeId: string }>();
  const { user, isAdmin, loading: authLoading } = useAuth();
  const { createProduct: createProductMutation } = useProducts({ storeId: storeId || "" });

  // Fetch store name for display
  const { data: store } = useQuery({
    queryKey: ['admin-store-name', storeId],
    queryFn: async () => {
      if (!storeId) return null;
      const { data } = await supabase
        .from('store_profiles')
        .select('id, store_name')
        .eq('id', storeId)
        .single();
      return data;
    },
    enabled: !!storeId,
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user || !isAdmin) {
      navigate("/");
    }
  }, [user, isAdmin, authLoading, navigate]);

  if (!user || !isAdmin || !storeId) return null;

  const handleProductCreate = async (data: ProductFormData) => {
    try {
      const { data: occasionData, error: occasionError } = await supabase
        .from('occasions')
        .select('id, name')
        .in('name', data.occasions);

      if (occasionError) throw occasionError;

      const transformedData = {
        title: data.title,
        description: data.description,
        category: data.category,
        active: data.active,
        image_url: data.sizes[0]?.image_url,
        gallery_image_urls: data.gallery_image_urls || [],
        occasions: occasionData || [],
        sizes: data.sizes.map(size => ({
          size_name: size.size_name,
          price: size.price,
          image_url: size.use_product_image ? data.sizes[0]?.image_url : size.image_url,
          use_product_image: size.use_product_image
        }))
      };

      const createdProduct = await createProductMutation.mutateAsync(transformedData);

      if (createdProduct?.id) {
        navigate(`/admin/stores/${storeId}`);
      } else {
        toast.error("Failed to create product. Please try again.");
      }
    } catch (error: any) {
      console.error("Error creating product:", error);
      toast.error(error.message || "Failed to create product");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            onClick={() => navigate(`/admin/stores/${storeId}`)}
            variant="ghost"
            className="gap-2 bg-[#967F81] hover:bg-[#843329]/10 hover:text-brand-primary"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to {store?.store_name || 'Store'}
          </Button>
        </div>

        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Add Product
          </h1>
          <p className="text-muted-foreground mb-8">
            Adding product to <span className="font-medium">{store?.store_name || 'store'}</span>
          </p>
          <ProductForm
            onSubmit={handleProductCreate}
            isLoading={createProductMutation.isPending}
            initialData={{
              active: true,
              occasions: [],
              sizes: [{
                size_name: "Standard",
                price: 0,
                image_url: null,
                use_product_image: true
              }]
            }}
          />
        </div>
      </div>
    </AdminLayout>
  );
}
